
/**
 * Load balancing strategies for distributed service management
 * Handles request distribution across multiple service instances
 */

import { logInfo, logError } from './logger';

export type LoadBalancingStrategy = 'round-robin' | 'weighted' | 'least-connections' | 'ip-hash';

export interface ServiceInstance {
  id: string;
  url: string;
  weight: number;
  region: string;
  status: 'active' | 'draining' | 'inactive';
  connections: number;
  healthChecks: {
    lastCheck: Date;
    status: 'healthy' | 'degraded' | 'unhealthy';
    responseTime: number;
  }
}

export interface LoadBalancerConfig {
  strategy: LoadBalancingStrategy;
  healthCheckInterval: number; // milliseconds
  failoverEnabled: boolean;
  serviceInstances: ServiceInstance[];
}

// Default load balancer configuration
const defaultConfig: LoadBalancerConfig = {
  strategy: 'weighted',
  healthCheckInterval: 30000, // 30 seconds
  failoverEnabled: true,
  serviceInstances: [
    {
      id: 'primary',
      url: 'https://api-primary.example.com',
      weight: 100,
      region: 'us-east-1',
      status: 'active',
      connections: 0,
      healthChecks: {
        lastCheck: new Date(),
        status: 'healthy',
        responseTime: 150
      }
    },
    {
      id: 'secondary',
      url: 'https://api-secondary.example.com',
      weight: 50,
      region: 'us-west-1',
      status: 'active',
      connections: 0,
      healthChecks: {
        lastCheck: new Date(),
        status: 'healthy',
        responseTime: 180
      }
    }
  ]
};

class LoadBalancer {
  private config: LoadBalancerConfig;
  private roundRobinCounter: number = 0;
  private healthCheckTimer: number | null = null;

  constructor(config: Partial<LoadBalancerConfig> = {}) {
    this.config = {
      ...defaultConfig,
      ...config,
      serviceInstances: [...(config.serviceInstances || defaultConfig.serviceInstances)]
    };
    
    this.startHealthChecks();
  }

  /**
   * Get the next service instance based on the configured strategy
   */
  public getNextInstance(userId?: string): ServiceInstance | null {
    const activeInstances = this.config.serviceInstances.filter(i => i.status === 'active');
    
    if (activeInstances.length === 0) {
      logError('Load balancer error', 'No active service instances available');
      return null;
    }

    switch (this.config.strategy) {
      case 'round-robin':
        return this.roundRobinStrategy(activeInstances);
      case 'weighted':
        return this.weightedStrategy(activeInstances);
      case 'least-connections':
        return this.leastConnectionsStrategy(activeInstances);
      case 'ip-hash':
        return this.ipHashStrategy(activeInstances, userId);
      default:
        return activeInstances[0];
    }
  }

  /**
   * Update the status of a service instance
   */
  public updateInstanceStatus(instanceId: string, status: 'active' | 'draining' | 'inactive'): void {
    const instance = this.config.serviceInstances.find(i => i.id === instanceId);
    if (instance) {
      instance.status = status;
      logInfo('Load balancer', `Updated instance ${instanceId} status to ${status}`);
    }
  }

  /**
   * Update the connection count for a service instance
   */
  public updateConnectionCount(instanceId: string, connections: number): void {
    const instance = this.config.serviceInstances.find(i => i.id === instanceId);
    if (instance) {
      instance.connections = connections;
    }
  }

  /**
   * Get all service instances
   */
  public getAllInstances(): ServiceInstance[] {
    return [...this.config.serviceInstances];
  }

  /**
   * Start periodic health checks
   */
  private startHealthChecks(): void {
    if (this.healthCheckTimer !== null) {
      window.clearInterval(this.healthCheckTimer);
    }

    this.healthCheckTimer = window.setInterval(() => {
      this.performHealthChecks();
    }, this.config.healthCheckInterval);
    
    // Run an initial health check
    this.performHealthChecks();
  }

  /**
   * Perform health checks on all service instances
   */
  private async performHealthChecks(): Promise<void> {
    for (const instance of this.config.serviceInstances) {
      try {
        const startTime = Date.now();
        
        // Simulate health check for now - in production this would be a real HTTP request
        const isHealthy = Math.random() > 0.1; // 90% chance of being healthy
        const responseTime = Math.floor(Math.random() * 200) + 100; // 100-300ms response time
        
        instance.healthChecks = {
          lastCheck: new Date(),
          status: isHealthy ? 'healthy' : 'degraded',
          responseTime: responseTime
        };

        if (!isHealthy && this.config.failoverEnabled) {
          if (instance.status === 'active') {
            instance.status = 'draining';
            logError('Load balancer', `Instance ${instance.id} health check failed, marked as draining`);
          }
        } else if (instance.status === 'draining') {
          instance.status = 'active';
          logInfo('Load balancer', `Instance ${instance.id} health check recovered, marked as active`);
        }
      } catch (error) {
        logError('Load balancer', `Health check failed for ${instance.id}: ${error}`);
        
        if (this.config.failoverEnabled) {
          instance.status = 'inactive';
          instance.healthChecks = {
            lastCheck: new Date(),
            status: 'unhealthy',
            responseTime: 0
          };
        }
      }
    }
  }

  /**
   * Round-robin load balancing strategy
   */
  private roundRobinStrategy(instances: ServiceInstance[]): ServiceInstance {
    this.roundRobinCounter = (this.roundRobinCounter + 1) % instances.length;
    return instances[this.roundRobinCounter];
  }

  /**
   * Weighted load balancing strategy
   */
  private weightedStrategy(instances: ServiceInstance[]): ServiceInstance {
    const totalWeight = instances.reduce((sum, i) => sum + i.weight, 0);
    let random = Math.random() * totalWeight;
    
    for (const instance of instances) {
      random -= instance.weight;
      if (random <= 0) {
        return instance;
      }
    }
    
    return instances[0];
  }

  /**
   * Least connections load balancing strategy
   */
  private leastConnectionsStrategy(instances: ServiceInstance[]): ServiceInstance {
    return instances.sort((a, b) => a.connections - b.connections)[0];
  }

  /**
   * IP-hash (or userId hash) load balancing strategy for session stickiness
   */
  private ipHashStrategy(instances: ServiceInstance[], userId?: string): ServiceInstance {
    if (!userId) {
      return this.weightedStrategy(instances);
    }
    
    // Simple hash function for userId
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = ((hash << 5) - hash) + userId.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    
    // Ensure positive number
    hash = Math.abs(hash);
    
    return instances[hash % instances.length];
  }
}

// Create singleton instance of load balancer
export const loadBalancer = new LoadBalancer();

// Method to get service URL based on current load balancing strategy
export const getServiceUrl = (serviceName: string, userId?: string): string => {
  const instance = loadBalancer.getNextInstance(userId);
  if (!instance) {
    // Fallback to default URL if no instances available
    return 'https://api-default.example.com';
  }
  
  loadBalancer.updateConnectionCount(instance.id, instance.connections + 1);
  logInfo('LoadBalancer', `Selected instance ${instance.id} for service ${serviceName}`);
  
  return instance.url;
};

// Method to release connection when request is complete
export const releaseConnection = (instanceId: string): void => {
  const instance = loadBalancer.getAllInstances().find(i => i.id === instanceId);
  if (instance && instance.connections > 0) {
    loadBalancer.updateConnectionCount(instance.id, instance.connections - 1);
  }
};
